import { ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';

// material-ui
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// project imports
import Logo from 'components/logo';
import AuthCard from './AuthCard';
import LoginProvider from './LoginProvider';

import useAuth from 'hooks/useAuth';

// assets
import AuthBackground from './AuthBackground';

interface Props {
  children: ReactElement;
  logoUrl?: string; // Optional custom logo URL
  companyName?: string; // Optional company name
  hideLogo?: boolean; // Optional: hide default logo
  loading?: boolean; // Optional: show loading skeleton
}

// ==============================|| AUTHENTICATION - WRAPPER ||============================== //

export default function AuthWrapper({ children, logoUrl, companyName, hideLogo, loading }: Props) {
  const { isLoggedIn } = useAuth();
  const [searchParams] = useSearchParams();
  const authParam = searchParams.get("auth") || "";

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <AuthBackground />
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        sx={{ minHeight: "100vh" }}
      >
        {/* Logo centered */}
        <Box sx={{ mb: 3 }}>
          {loading ? (
            <Stack alignItems="center" spacing={1}>
              {/* Logo Skeleton */}
              <Skeleton variant="rectangular" width={60} height={60} sx={{ borderRadius: '50%', mb: 1 }} animation="wave" />
              {/* Company Name Skeleton */}
              <Skeleton variant="text" width={180} height={40} animation="wave" />
            </Stack>
          ) : logoUrl ? (
            <Stack alignItems="center" spacing={1}>
              <Box
                component="img"
                src={logoUrl}
                alt={companyName || "Company Logo"}
                sx={{
                  width: 150,
                  height: 'auto',
                  objectFit: 'contain'
                }}
              />
              {companyName && (
                <Typography variant="h4" color="textPrimary">
                  {companyName}
                </Typography>
              )}
            </Stack>
          ) : companyName ? (
            <Typography variant="h3" color="textPrimary">
              {companyName}
            </Typography>
          ) : !hideLogo ? (
            <Logo to="/" authVariant sx={{ justifyContent: 'center' }} />
          ) : null}
        </Box>

        {/* Login Card */}
        <AuthCard>{children}</AuthCard>

        {/* Social login (if not logged in) */}
        {!isLoggedIn && (
          <Box
            sx={{
              maxWidth: { xs: 400, sm: 475 },
              mt: 3,
              "& > *": { flexGrow: 1, flexBasis: "50%" },
            }}
          >
            <Grid size={12}>
              <LoginProvider currentLoginWith={authParam} />
            </Grid>
          </Box>
        )}
      </Grid>
    </Box>
  );
}
